import React from 'react';
import { TerrainAlertProps } from './types';
import { selectTerrainMode, selectWaypoints, useGetTerrainAwareMutation } from '@src/features/order/pages/upsert-mission-page/redux';
import { TerrainMode } from '@src/features/order/pages/upsert-mission-page/redux/extra-store/types';
import { terrainCacheKey } from '@src/features/order/pages/upsert-mission-page/config/cache-keys';
import { useSelector } from 'react-redux';
import { ArcAlert, ArcText } from '@src/core/ui';
import { analyzeIntervals, calculateDistance } from '@src/features/order/pages/upsert-mission-page/utils';

const TerrainAlert: React.FC<TerrainAlertProps> = () => {

  const wps = useSelector(selectWaypoints);
  const terrainMode = useSelector(selectTerrainMode);
  const isAboveTerrain = terrainMode === TerrainMode.ABOVE_TERRAIN;
  const [, { data = [], isLoading }] = useGetTerrainAwareMutation({ fixedCacheKey: terrainCacheKey });

  const wpDist = React.useMemo(() => calculateDistance(wps), [wps]);
  const terrainData = React.useMemo(() => calculateDistance(data, data[0]?.altitude), [data]);

  const droneData = React.useMemo(() => {
    if (!isAboveTerrain || data.length === 0) return wpDist;

    const totalDroneDist = wpDist.length > 0 ? wpDist[wpDist.length - 1].distance : 0;

    return terrainData.map((point) => {
      const d = point.distance;
      let agl = Number(wpDist[0]?.altitude ?? 0);

      for (let i = 0; i < wpDist.length - 1; i++) {
        const segStart = wpDist[i].distance;
        const segEnd = wpDist[i + 1].distance;
        if (d >= segStart && d <= segEnd) {
          const segLen = segEnd - segStart;
          const ratio = segLen === 0 ? 0 : (d - segStart) / segLen;
          agl = Number(wpDist[i].altitude) + (Number(wpDist[i + 1].altitude) - Number(wpDist[i].altitude)) * ratio;
          break;
        }
      }

      if (d > totalDroneDist) {
        agl = Number(wpDist[wpDist.length - 1]?.altitude ?? 0);
      }

      return { ...point, altitude: Number(point.altitude) + agl };
    });
  }, [isAboveTerrain, data, wpDist, terrainData]);

  const intervals = analyzeIntervals(droneData, terrainData);

  if (isLoading) return null;

  if (intervals.length > 0) {
    return (
      <ArcAlert severity='warning'>
        <ArcText.h6 color='warning.main'> 
          Terrain collision detected. Current altitudes are unsafe, adjust them before takeoff.
        </ArcText.h6>
      </ArcAlert> 
    );
  }

  return (
    <ArcAlert severity='success'>
      <ArcText.h6 color='success.main'> 
        No terrain collision detected. Current altitudes are safe.
      </ArcText.h6>
    </ArcAlert>

  );
};

export default TerrainAlert;